import type { OperationTestResult } from '@/lib/types/test-cases'

/**
 * The generator wraps node-typed params on the wire as {type, data} markers
 * (tree/linkedList/doublyLinkedList/class-instance). A result's `inputs` holds
 * that raw wire form, while post-call state is serialized to canonical arrays
 * — so rendering the two side by side would show `head = {"type":...}` above
 * `head = [1,2,3]` and read as if the call replaced the list with an array.
 * Unwrapping here keeps every param row in one representation.
 */
const NODE_MARKER_TYPES = new Set(['tree', 'linkedList', 'doublyLinkedList'])

/**
 * Peeling the wrapper is not enough on its own: the authored `data` is one of
 * several arrays that build the SAME node structure, while the post-call side
 * comes back from tree_to_array/list_to_array in exactly one of them. Show the
 * authored form verbatim and an untouched param reads as mutated —
 * `null` above `[]`, or `[1,2,3,null,null]` above `[1,2,3]`. So put the input
 * side through the round-trip the builders and serializers agree on.
 */
function canonicalizeNodeData(type: string, data: unknown): unknown {
  // array_to_tree/array_to_list build nothing from a missing or empty array,
  // and the matching serializer renders that absent node as [].
  if (data === null || data === undefined) {
    return []
  }
  if (!Array.isArray(data) || type !== 'tree') {
    return data
  }
  // A tree's builder ignores trailing nulls (and refuses an array whose head is
  // null outright); tree_to_array trims them straight back off.
  if (data[0] === null) {
    return []
  }
  let end = data.length
  while (end > 0 && data[end - 1] === null) {
    end--
  }
  return end === data.length ? data : data.slice(0, end)
}

export function unwrapInputMarker(value: unknown): unknown {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return value
  }
  const marker = value as { type?: unknown; data?: unknown }
  if (typeof marker.type !== 'string') {
    return value
  }
  if (NODE_MARKER_TYPES.has(marker.type)) {
    return canonicalizeNodeData(marker.type, marker.data)
  }
  // A class-instance marker legitimately carries no `data` ("no constructor
  // args" — see materializeClassArg), and unwrapping that to undefined would
  // render the row as the literal "undefined". Keep the marker in that case:
  // it is at least truthful.
  if (marker.type === 'class-instance' && 'data' in marker) {
    return marker.data
  }
  return value
}

/**
 * Record-level form, for the callers that hand a whole param map somewhere
 * else — the AI interviewer, which otherwise reads the raw wire marker next to
 * a canonical `inputsAfter` array and draws the same wrong conclusion a reader
 * would.
 */
export function unwrapInputMarkers(
  inputs: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(inputs).map(([key, value]) => [
      key,
      unwrapInputMarker(value),
    ]),
  )
}

/**
 * Stringify a value for display in compact format.
 * Keeps simple structures on one line, only breaks for complex/nested data.
 */
export function stringifyValue(value: unknown): string {
  if (value === undefined) {
    return 'undefined'
  }
  if (value === null) {
    return 'null'
  }

  try {
    const jsonString = JSON.stringify(value)

    // For simple values (primitives, short arrays/objects), return as-is
    if (jsonString.length <= 80 && !jsonString.includes('\n')) {
      return jsonString
    }

    // For complex structures, use compact formatting
    // This will indent nested structures but keep arrays/objects compact when possible
    return formatCompactJSON(value, 0, 80)
  } catch {
    return String(value)
  }
}

/**
 * Format JSON in a compact way - only break lines when necessary.
 */
function formatCompactJSON(
  value: unknown,
  indent: number,
  maxLineLength: number,
): string {
  const indentStr = '  '.repeat(indent)
  const nextIndent = indent + 1
  const nextIndentStr = '  '.repeat(nextIndent)

  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value)
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '[]'
    }

    // Try single-line format first
    const singleLine = `[${value.map((v) => formatCompactJSON(v, 0, maxLineLength)).join(', ')}]`
    if (singleLine.length <= maxLineLength) {
      return singleLine
    }

    // Multi-line format
    const items = value.map(
      (v) =>
        `${nextIndentStr}${formatCompactJSON(v, nextIndent, maxLineLength)}`,
    )
    return `[\n${items.join(',\n')}\n${indentStr}]`
  }

  // Object
  const entries = Object.entries(value)
  if (entries.length === 0) {
    return '{}'
  }

  // Try single-line format first
  const singleLine = `{${entries.map(([k, v]) => `${JSON.stringify(k)}: ${formatCompactJSON(v, 0, maxLineLength)}`).join(', ')}}`
  if (singleLine.length <= maxLineLength) {
    return singleLine
  }

  // Multi-line format
  const items = entries.map(
    ([k, v]) =>
      `${nextIndentStr}${JSON.stringify(k)}: ${formatCompactJSON(v, nextIndent, maxLineLength)}`,
  )
  return `{\n${items.join(',\n')}\n${indentStr}}`
}

/**
 * Format an operation as a method call, e.g., "obj.create_grant(0, "a", 15, 100)"
 */
export function formatOperationCode(
  operation: OperationTestResult,
  index: number,
): string {
  if (!operation.method) {
    return `operation ${index + 1}`
  }

  const args = operation.args ?? []
  // Reuse stringifyValue (the canonical stringifier in this file) for each arg
  // rather than a bespoke implementation. The previous local formatArgument
  // fell through to String(value) for plain objects, rendering them as the
  // useless "[object Object]"; stringifyValue JSON-serializes them correctly.
  const argsStr = args.map((arg) => stringifyValue(arg)).join(', ')
  return `obj.${operation.method}(${argsStr})`
}
